import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, TrendingUp, Users, Globe, Award, Bell, Mail, Save, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { assistantsAnalytics, assistantsAlertsConfigGet, assistantsAlertsConfigPut, assistantsAlertsHistory, assistantsAlertsTest } from "@/lib/api";

const RANGES = [
  { key: 7, label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];

const KIND_LABELS = {
  "website-audit":   { label: "Website Auditor",   color: "#4f46e5" },
  "proposal-writer": { label: "Proposal Writer",   color: "#059669" },
  "sales-coach":     { label: "Sales Coach",       color: "#d97706" },
};

export default function AdminAssistantsAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "AI Assistants Analytics — Admin";
    setLoading(true); setErr("");
    assistantsAnalytics(days)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Not authorized. Sign in as admin."))
      .finally(() => setLoading(false));
  }, [days]);

  const totals = data?.totals || { runs: 0, leads: 0, days: 0 };
  const perKind = data?.per_kind || {};
  const timeseries = data?.timeseries || [];
  const topDomains = data?.top_audit_domains || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <Link to="/portal/assistants" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="analytics-back">
          <ArrowLeft className="w-4 h-4" /> Back to hub
        </Link>
        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-indigo-700 font-bold border border-indigo-200 bg-indigo-50 rounded-full px-3 py-1">
              <TrendingUp className="w-3.5 h-3.5" /> Admin · analytics
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900" data-testid="analytics-title">
              AI Assistants Analytics
            </h1>
            <p className="mt-2 text-slate-600 text-sm max-w-2xl">
              Every run of every assistant, over the last {days} days.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setDays(r.key)}
                data-testid={`analytics-range-${r.key}`}
                className={`text-xs uppercase font-bold px-3 py-1.5 rounded-full border transition ${
                  days === r.key
                    ? "bg-indigo-600 text-white border-indigo-700"
                    : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"
                }`}>
                {r.label}
              </button>
            ))}
            <Link to="/admin/iris-analytics" data-testid="link-iris-analytics"
              className="text-xs uppercase font-bold px-3 py-1.5 rounded-full border border-sky-300 text-sky-800 hover:bg-sky-50 flex items-center gap-1">
              Iris analytics →
            </Link>
          </div>
        </div>

        {err && <div className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="analytics-error">{err}</div>}
        {loading && !data && (
          <div className="mt-12 text-slate-500 flex items-center gap-2 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading analytics…
          </div>
        )}

        {data && (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Tile label="Total runs" value={totals.runs} icon={Sparkles} tone="indigo" tid="analytics-total-runs" />
              <Tile label="Leads captured" value={totals.leads} icon={Users} tone="emerald" tid="analytics-total-leads" />
              <Tile label="Lead rate" value={totals.runs ? `${Math.round(totals.leads / totals.runs * 100)}%` : "—"} icon={TrendingUp} tone="amber" tid="analytics-lead-rate" />
              <Tile label="Domains audited" value={topDomains.length} icon={Globe} tone="slate" tid="analytics-domains" />
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(perKind).map(([k, v]) => {
                const meta = KIND_LABELS[k] || { label: k, color: "#64748b" };
                return (
                  <div key={k} data-testid={`analytics-kind-${k}`}
                    className="rounded-2xl bg-white border border-slate-200 p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-bold text-slate-900">{meta.label}</div>
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Runs</div>
                        <div className="text-2xl font-bold text-slate-900">{v.runs}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Leads</div>
                        <div className="text-2xl font-bold text-slate-900">{v.leads}</div>
                      </div>
                      {"avg_score" in v && (
                        <div className="col-span-2">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Avg score</div>
                          <div className="text-2xl font-bold" style={{ color: meta.color }}>
                            {v.avg_score ?? "—"}{v.avg_score != null && <span className="text-slate-400 text-sm"> / 100</span>}
                          </div>
                        </div>
                      )}
                      {"avg_quote" in v && (
                        <div className="col-span-2">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Avg quote hint</div>
                          <div className="text-2xl font-bold" style={{ color: meta.color }}>
                            {v.avg_quote != null ? `$${Number(v.avg_quote).toLocaleString()}` : "—"}
                          </div>
                        </div>
                      )}
                      {v.grade_dist && (
                        <div className="col-span-2 mt-1">
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Grade mix</div>
                          <div className="flex gap-1 text-[11px] font-bold">
                            {Object.entries(v.grade_dist).map(([g, c]) => (
                              <div key={g} className="flex-1 rounded px-1.5 py-1 text-center"
                                style={{
                                  background: g === "A" || g === "B" ? "#ecfdf5" :
                                              g === "C" ? "#fffbeb" : "#fef2f2",
                                  color: g === "A" || g === "B" ? "#065f46" :
                                          g === "C" ? "#92400e" : "#9f1239",
                                }}>
                                {g}·{c}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {timeseries.length > 0 && (
              <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6" data-testid="analytics-timeseries">
                <div className="text-sm font-bold text-slate-900">Daily runs</div>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={timeseries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tick={{ fill: "#64748b" }} />
                      <YAxis stroke="#94a3b8" fontSize={11} tick={{ fill: "#64748b" }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="website-audit" stackId="a" fill="#4f46e5" name="Auditor" />
                      <Bar dataKey="proposal-writer" stackId="a" fill="#059669" name="Proposal" />
                      <Bar dataKey="sales-coach" stackId="a" fill="#d97706" name="Coach" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {topDomains.length > 0 && (
              <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6" data-testid="analytics-top-domains">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <Award className="w-4 h-4 text-amber-600" /> Top domains audited
                </div>
                <div className="mt-4 space-y-1">
                  {topDomains.map((d, i) => (
                    <div key={i} data-testid={`analytics-domain-${i}`}
                      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded bg-indigo-50 text-indigo-700 text-xs font-bold grid place-items-center">{i + 1}</div>
                        <div className="text-sm text-slate-800 font-medium">{d.domain}</div>
                      </div>
                      <div className="text-sm font-bold text-slate-900">{d.count} runs</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <AlertsPanel />
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}


function AlertsPanel() {
  const [cfg, setCfg] = useState(null);
  const [resolved, setResolved] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await assistantsAlertsConfigGet();
      setCfg(r.config);
      setResolved(r.resolved_recipients);
      const h = await assistantsAlertsHistory(10);
      setHistory(h.items || []);
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || "Could not load alert config" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true); setFlash(null);
    try {
      const r = await assistantsAlertsConfigPut({
        runs_per_day_spike: Number(cfg.runs_per_day_spike),
        quote_hint_min: Number(cfg.quote_hint_min),
        score_low_threshold: Number(cfg.score_low_threshold),
        recipients: (cfg.recipients || []).map((e) => e.trim()).filter((e) => e && e.includes("@")),
        include_role_admins: !!cfg.include_role_admins,
        enabled: !!cfg.enabled,
        cooldown_minutes: Number(cfg.cooldown_minutes),
        slack_webhook_url: (cfg.slack_webhook_url || "").trim(),
      });
      setCfg(r.config);
      setResolved(r.resolved_recipients);
      setFlash({ ok: true, msg: "Alert settings saved." });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || "Save failed" });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true); setFlash(null);
    try {
      const r = await assistantsAlertsTest();
      if (r.sent) {
        const parts = [];
        if (r.recipients && r.recipients.length) parts.push(`Email → ${r.recipients.join(", ")}`);
        if ((r.channels || []).includes("slack")) parts.push("Slack ✓");
        setFlash({ ok: true, msg: `Test sent. ${parts.join(" · ")}` });
      } else setFlash({ ok: false, msg: r.reason || (r.errors || []).join("; ") || "Test failed" });
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  if (loading || !cfg) return (
    <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 flex items-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading alert settings…
    </div>
  );

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const updateRecipients = (val) => upd("recipients", val.split(/[,\n]/).map((e) => e.trim()).filter(Boolean));

  return (
    <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6" data-testid="alerts-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 grid place-items-center">
            <Bell className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900" data-testid="alerts-title">Email alerts</div>
            <div className="text-xs text-slate-500">Fires when the auditor spikes, a big quote is drafted, or a very low-scoring site comes through.</div>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => upd("enabled", e.target.checked)}
            data-testid="alerts-enabled" className="w-4 h-4" />
          <span className="font-semibold text-slate-800">Alerts enabled</span>
        </label>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <NumField label="Runs per day spike" hint="Emails when a kind exceeds this in 24h"
          v={cfg.runs_per_day_spike} on={(v) => upd("runs_per_day_spike", v)} tid="alerts-spike" />
        <NumField label="Quote hint min ($)" hint="Alert on high-value proposal drafts"
          v={cfg.quote_hint_min} on={(v) => upd("quote_hint_min", v)} tid="alerts-quote-min" />
        <NumField label="Low-score threshold" hint="Audit scores below this trigger a lead alert"
          v={cfg.score_low_threshold} on={(v) => upd("score_low_threshold", v)} tid="alerts-score-low" />
        <NumField label="Cooldown (minutes)" hint="Anti-spam gate per alert kind"
          v={cfg.cooldown_minutes} on={(v) => upd("cooldown_minutes", v)} tid="alerts-cooldown" />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Recipient emails</label>
          <textarea value={(cfg.recipients || []).join("\n")}
            onChange={(e) => updateRecipients(e.target.value)}
            data-testid="alerts-recipients"
            rows={3} placeholder="one@example.com, two@example.com"
            className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500 text-sm font-mono" />
          <div className="mt-1 text-[11px] text-slate-500">Comma- or newline-separated.</div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Also include</label>
          <label className="mt-2 flex items-start gap-2 text-sm rounded border border-slate-200 p-3">
            <input type="checkbox" checked={!!cfg.include_role_admins}
              onChange={(e) => upd("include_role_admins", e.target.checked)}
              data-testid="alerts-include-role-admins" className="mt-0.5 w-4 h-4" />
            <div>
              <div className="font-semibold text-slate-800">Every user with role=admin</div>
              <div className="text-[11px] text-slate-500">Automatically expands to all active admins.</div>
            </div>
          </label>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs uppercase tracking-widest font-bold text-slate-500 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-[#4A154B]"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/></svg>
          Slack incoming webhook URL (optional)
        </label>
        <input type="url" value={cfg.slack_webhook_url || ""}
          onChange={(e) => upd("slack_webhook_url", e.target.value)}
          data-testid="alerts-slack-url"
          placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXX"
          className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500 text-sm font-mono" />
        <div className="mt-1 text-[11px] text-slate-500">
          Get one at Slack → Apps → Incoming Webhooks. Alerts post to this channel alongside email.
        </div>
      </div>

      <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="alerts-resolved">
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Will email</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {(resolved || []).map((e, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs bg-white border border-slate-200 rounded-full px-2.5 py-1">
              <Mail className="w-3 h-3 text-slate-500" /> {e}
            </span>
          ))}
          {resolved.length === 0 && <span className="text-xs text-rose-600">No recipients configured.</span>}
        </div>
      </div>

      {flash && (
        <div className={`mt-4 rounded-lg p-3 text-sm flex items-center gap-2 ${
          flash.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"
        }`} data-testid="alerts-flash">
          {flash.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {flash.msg}
        </div>
      )}

      <div className="mt-5 flex gap-2 flex-wrap">
        <button onClick={save} disabled={saving} data-testid="alerts-save"
          className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded transition">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </button>
        <button onClick={sendTest} disabled={testing || !cfg.enabled} data-testid="alerts-test"
          className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-indigo-400 text-slate-700 hover:text-indigo-700 text-sm font-bold px-4 py-2 rounded transition disabled:opacity-50">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test email
        </button>
      </div>

      {history.length > 0 && (
        <div className="mt-6" data-testid="alerts-history">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Recent alerts</div>
          <div className="space-y-1">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-slate-800">{h.subject}</span>
                </div>
                <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function NumField({ label, hint, v, on, tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</label>
      <input type="number" value={v ?? ""} onChange={(e) => on(e.target.value)}
        data-testid={tid}
        className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone, tid }) {
  const bg = { indigo: "#eef2ff", emerald: "#ecfdf5", amber: "#fffbeb", slate: "#f8fafc" }[tone] || "#f8fafc";
  const fg = { indigo: "#4f46e5", emerald: "#059669", amber: "#d97706", slate: "#475569" }[tone] || "#475569";
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: bg }}>
          <Icon className="w-4 h-4" style={{ color: fg }} />
        </div>
      </div>
      <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
